import React from "react";
import clsx from "clsx";

import { createPluggable } from "pluggable-react";
import { InputProps } from "../../../types/FormTypes";
import InputBox from "../InputBox/InputBox";
import Icon from "../../common/Icon/Icon";
import Button from "../../common/Button/Button";
import NumberInput from "./NumberInput";
import styles from "./TextInput.module.scss";
import { withRefProp } from "../../../utils/withRefProp";

export interface TextInputProps
  extends Omit<React.ComponentPropsWithoutRef<"input">, "onChange" | "value" | "onBlur" | "onFocus">,
    InputProps<string> {
  ref?: React.Ref<HTMLInputElement>;
  placeholder?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  clearable?: boolean;
  onClear?: () => void;
  clearAriaLabel?: string;
}

const DefaultTextInput = withRefProp((props: TextInputProps) => {
  const {
    className,
    value,
    onChange,
    onValueChange,
    placeholder,
    disabled = false,
    error = false,
    fullWidth = false,
    type = "text",
    startAdornment,
    endAdornment,
    clearable = false,
    onClear,
    clearAriaLabel = "Clear",
    ref,
    ...otherProps
  } = props;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
    onValueChange?.(e.target.value);
  };
  const hasValue = typeof value === "string" ? value.length > 0 : Boolean(value);
  const showClear = clearable && !disabled && hasValue;
  const clearValue = () => {
    onValueChange?.("");
    onClear?.();
  };

  if (type === "number") {
    return <NumberInput {...props} />;
  }

  return (
    <InputBox
      forwardToInput={false}
      className={clsx("flex items-center gap-1")}
      fullWidth={fullWidth}
      error={error}
      disabled={disabled}
    >
      {startAdornment ? <span className={styles.startAdornment}>{startAdornment}</span> : null}
      <input
        ref={ref}
        className={clsx(styles.textInput, className)}
        value={value}
        onChange={handleChange}
        onBlur={props.onBlur}
        placeholder={placeholder}
        disabled={disabled}
        type={type}
        data-deci-textinput="true"
        data-full-width={fullWidth ? "true" : "false"}
        {...otherProps}
      />
      {showClear ? (
        <Button
          icon
          type="button"
          label={clearAriaLabel}
          tooltip={false}
          size="sm"
          className={styles.clearButton}
          onClick={clearValue}
        >
          <Icon name="clear" />
        </Button>
      ) : null}
      {endAdornment ? <span className={styles.endAdornment}>{endAdornment}</span> : null}
    </InputBox>
  );
});

const TextInput = createPluggable<TextInputProps>("deci.textinput", DefaultTextInput);

export default TextInput;
